import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import HomePage from './components/HomePage';
import DownloadPage from './pages/DownloadPage';
import AppRedirect from './components/AppRedirect';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Home, download and the deep-link fallback are the landing routes and ship in
// the main chunk. Everything else is reached by a link in an email or a QR
// code, so it loads on demand instead of weighing on the landing pages.
const AuthAction = lazy(() => import('./components/AuthAction'));
const ReferralLanding = lazy(() => import('./components/ReferralLanding'));
const PaymentQrLanding = lazy(() => import('./components/PaymentQrLanding'));
const PaymentSuccess = lazy(() => import('./components/PaymentSuccess'));
const PaymentFailed = lazy(() => import('./components/PaymentFailed'));
const AppCallback = lazy(() => import('./components/AppCallback'));
const BridgeTosCallback = lazy(() => import('./pages/BridgeTosCallback'));

const ROUTE_FALLBACK = (
  <div className="grid min-h-screen place-items-center bg-[#ffffff]" role="status" aria-label="Loading">
    <span className="h-8 w-8 animate-spin rounded-full border-2 border-[#e6e6e6] border-t-[#10b981]" />
  </div>
);

function AuthActionLegacyRedirect() {
  const location = useLocation();
  return <Navigate to={`/auth/action${location.search}${location.hash}`} replace />;
}

/**
 * Public site only (yunlen.app): home, download, app deep-link fallbacks.
 * Admin login and dashboard are not included — those deploy to opps1.yunlen.app.
 */
function AppPublicContent() {
  return (
    <div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Suspense fallback={ROUTE_FALLBACK}>
        <Routes>
          <Route path="/ref/:code" element={<ReferralLanding />} />
          <Route path="/r/:code" element={<ReferralLanding />} />
          <Route path="/invite/:code" element={<ReferralLanding />} />
          <Route path="/send/:userId" element={<PaymentQrLanding />} />
          <Route path="/app" element={<AppRedirect />} />
          <Route path="/app/callback" element={<AppCallback />} />

          {/* Universal-link fallbacks when the app is not installed / desktop */}
          <Route path="/activity" element={<AppRedirect />} />
          <Route path="/activity/:transactionId" element={<AppRedirect />} />
          <Route path="/transaction" element={<AppRedirect />} />
          <Route path="/transaction/:transactionId" element={<AppRedirect />} />
          <Route path="/transactions" element={<AppRedirect />} />
          <Route path="/notifications" element={<AppRedirect />} />
          <Route path="/notification/:notificationId" element={<AppRedirect />} />
          <Route path="/request/:requestId" element={<AppRedirect />} />
          <Route path="/referral" element={<AppRedirect />} />
          <Route path="/receive" element={<AppRedirect />} />
          <Route path="/deposit" element={<AppRedirect />} />
          <Route path="/withdraw" element={<AppRedirect />} />
          <Route path="/exchange" element={<AppRedirect />} />
          <Route path="/accounts" element={<AppRedirect />} />
          <Route path="/bank-account-fee" element={<AppRedirect />} />
          <Route path="/cards" element={<AppRedirect />} />
          <Route path="/create-card" element={<AppRedirect />} />
          <Route path="/crypto" element={<AppRedirect />} />
          <Route path="/buy-crypto" element={<AppRedirect />} />
          <Route path="/sell-crypto" element={<AppRedirect />} />
          <Route path="/bills" element={<AppRedirect />} />
          <Route path="/pay-bill" element={<AppRedirect />} />
          <Route path="/verify-documents" element={<AppRedirect />} />
          <Route path="/verification-status" element={<AppRedirect />} />
          <Route path="/promo/:code" element={<AppRedirect />} />
          <Route path="/campaign/:id" element={<AppRedirect />} />
          <Route path="/feature/:name" element={<AppRedirect />} />
          <Route path="/home" element={<AppRedirect />} />
          <Route path="/main" element={<AppRedirect />} />

          <Route path="/auth/action" element={<AuthAction />} />
          <Route path="/__/auth/action" element={<AuthActionLegacyRedirect />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/failed" element={<PaymentFailed />} />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/download*" element={<DownloadPage />} />
          <Route path="/bridge-tos-callback" element={<BridgeTosCallback />} />

          <Route path="/" element={<HomePage />} />
          <Route path="*" element={<DownloadPage />} />
        </Routes>
      </Suspense>
    </div>
  );
}

function AppPublic() {
  return (
    // No AuthProvider: nothing on the public site reads the admin session, and
    // the provider pulled Firebase Auth, Firestore and Functions into the
    // landing bundle. AuthAction imports the Firebase it needs in its own chunk.
    <Router>
      <AppPublicContent />
    </Router>
  );
}

export default AppPublic;
